import _Object$keys from 'babel-runtime/core-js/object/keys';
import _extends from 'babel-runtime/helpers/extends';
import flatten from 'lodash/flatten';
import { stepModel } from '../step/step-model';
import { stepTypes, inputTypes } from '../step/step-constants';

var CLOZE_SPACER = '%SPACER%';

function isCloze(step) {
  return step.type === stepTypes.CLOZE;
}

function isClozeSpeech(step) {
  return stepModel.getInputType(step) === inputTypes.SPEAKING;
}

function isClozeTyping(step) {
  return stepModel.getInputType(step) === inputTypes.TYPING;
}

function isClozeSelect(step) {
  return stepModel.getInputType(step) === inputTypes.SELECT;
}

function isClozeDragDrop(step) {
  return step.behavior && step.behavior.optionMenu === 'shared';
}

// Parse, initialize, and store content data
function parseClozeData(step) {
  if (isClozeDragDrop(step)) {
    return parseClozeDragDrop(step);
  }
  if (isClozeSelect(step)) {
    return parseClozeSelect(step);
  }
  if (isClozeSpeech(step)) {
    return parseClozeSpeech(step);
  }
  if (isClozeTyping(step)) {
    return parseClozeTyping(step);
  }
}

/**
 * Cloze Speech data parser
 *   'augmentedChoices' - a collection of choice objects with added sentence data.
 *   Use choice 'fillin' options and sentence parts to build whole sentences.
 *   Join parts and remove spaces in front of ending punctuation.
 */
function parseClozeSpeech(step) {
  var content = step.content[0];
  var shuffle = stepModel.isRandomOrdering(step);
  var fillin = content.find(function (obj) {
    return obj.hasOwnProperty('options');
  });
  var choices = fillin ? fillin.options : [];
  var sentenceParts = content.map(function (item) {
    if (isSlot(item)) {
      item.text = CLOZE_SPACER;
    }
    return item;
  });

  var augmentedChoices = choices.map(function (choice) {
    var sentencePartsStrings = sentenceParts.map(function (sentencePart) {
      if (sentencePart.text) {
        return sentencePart.text;
      }
      if (!sentencePart.htmlText) {
        return '';
      }
      // Strip html tags for SRE
      return sentencePart.htmlText.replace(/<[^>]+>/g, '');
    });
    var parts = [].concat(sentencePartsStrings);
    parts[parts.indexOf(CLOZE_SPACER)] = choice.text;
    // Put the sentence together and remove all extra spaces
    var sentence = parts.join(' ').replace(/\s+(\W)/g, '$1');
    return _extends({}, choice, { sentence: sentence });
  });
  return {
    choices: shuffle ? stepModel.shuffleChoices(augmentedChoices) : augmentedChoices,
    sreSpeakables: flatten(augmentedChoices.map(function (o) {
      return [o.sentence, o.sreText];
    })),
    sentenceParts: sentenceParts
  };
}

function parseClozeDragDrop(step) {
  var doShuffle = stepModel.isRandomOrdering(step);
  // set forceShuffle to true when doing shuffle-related debugging on QA envs
  var forceShuffle = false;
  var dragChoices = getClozeDragDropAnswers(step).map(function (o) {
    return o.options[0];
  });
  return {
    dragChoices: doShuffle ? stepModel.shuffleChoices(dragChoices, forceShuffle) : dragChoices,
    clozeRows: step.content
  };
}

function getClozeDragDropAnswers(step) {
  var content = flatten(step.content);
  return content.filter(function (o) {
    return o.hasOwnProperty('options');
  });
}

/**
 * Cloze 'select' types.
 *    Data includes an array of texts to build sentences and options for a drop-down list.
 *    Objects containing an 'options' property will hold the choices to populate the drop-down list.
 */
function parseClozeSelect(step) {
  var shuffle = stepModel.isRandomOrdering(step);
  // set forceShuffle to true when doing shuffle-related debugging on QA envs
  var forceShuffle = false;
  var contentRows = step.content.map(function (contentRow) {
    var rowParts = contentRow.map(function (rowPart) {
      var part = _extends({}, rowPart);
      if (shuffle && part.options) {
        part.options = stepModel.shuffleChoices(part.options, forceShuffle);
      }
      return part;
    });
    return rowParts;
  });
  return {
    clozeRows: contentRows
  };
}

/**
 * Cloze 'typing' types.
 *    'choices' non-interactive text, 'models' displayed to the user, obtained as an array from the
 *    objects with 'options' property
 *    'texts' building block texts to construct the sentences
 */
function parseClozeTyping(step) {
  var content = step.content[0];
  var shuffle = stepModel.isRandomOrdering(step);
  var fillin = content.find(function (obj) {
    return obj.hasOwnProperty('options');
  });
  var choices = fillin ? fillin.options : [];

  return {
    choices: choices && shuffle ? stepModel.shuffleChoices(choices) : choices,
    texts: content
  };
}

var isSlot = function isSlot(contentPart) {
  return contentPart.type === 'slot';
};

var getSlots = function getSlots(step) {
  var content = flatten(step.content);
  return content.filter(isSlot);
};

var convertCurlyQuotesToPlainQuotes = function convertCurlyQuotesToPlainQuotes(inString) {
  // replace any kind of curly single quote with plain single quote
  var cleanString = inString.replace(/[\u2018\u2019\u201A\u201B]/g, "'");
  // replace any kind of curly double quote with plain double quote
  cleanString = cleanString.replace(/[\u201C\u201D\u201E\u201F]/g, '"');
  return cleanString;
};

var getTypingAnswerId = function getTypingAnswerId(step, slotId, value) {
  var slot = step.content[0].find(function (p) {
    return p.id === slotId;
  });
  var cleanValue = convertCurlyQuotesToPlainQuotes(value);
  var item = slot.options.find(function (option) {
    var cleanOption = convertCurlyQuotesToPlainQuotes(option.text);
    return cleanOption === cleanValue;
  });
  return item && item.id;
};

var resolveTypingAnswers = function resolveTypingAnswers(step, values) {
  return _Object$keys(values).map(function (slotId) {
    var answer = values[slotId] && values[slotId].trim();
    var answerId = getTypingAnswerId(step, slotId, answer);
    if (!answerId) {
      return { correct: false, answer: answer };
    }
    var correct = isAnswerCorrect(step, slotId, { id: answerId, text: answer });
    return { correct: correct, answer: answerId };
  });
};

var resolveSelectAnswers = function resolveSelectAnswers(step, values) {
  return _Object$keys(values).map(function (slotId) {
    var answerId = values[slotId].trim();
    var correct = isAnswerCorrect(step, slotId, { id: answerId });
    return { correct: correct, answer: answerId };
  });
};

var resolveDragAndDropAnswers = function resolveDragAndDropAnswers(step, values) {
  var correctAnswers = getClozeDragDropAnswers(step);
  return _Object$keys(values).map(function (slotId) {
    var answer = values[slotId];
    var slot = correctAnswers.find(function (p) {
      return p.id === slotId;
    });
    var correct = slot.options[0].text === answer.text;
    return { correct: correct, answer: answer.id };
  });
};

var resolveSpeechAnswers = function resolveSpeechAnswers(step, values) {
  var answer = values[0]; // answer can be undefined here if stepValues is an empty array
  if (!answer) {
    return [];
  }
  var correct = !!step.correct.find(function (c) {
    return c === answer;
  });
  return [{ answer: answer, correct: correct }];
};

var isAnswerCorrect = function isAnswerCorrect(step, slotId, answer) {
  if (!answer) {
    return false;
  }
  var evaluationType = stepModel.getEvaluationType(step);
  var slots = getSlots(step);
  var slotIndex = slots.findIndex(function (s) {
    return s.id === slotId;
  });
  var slot = slots[slotIndex];
  var correct = step.correct[slotIndex];
  // for typing fitb, we might accept multiple answers so the correct answer might be oneguid:anotherguid:anotherguid
  //  as far as is known, the
  var correctAnswers = evaluationType === 'any' ? correct.split(':') : [correct];
  // if correct answer or answer with identical text is selected
  return !!correctAnswers.find(function (a) {
    return a === answer.id;
  }) || slot.options[0].text === answer.text;
};

var isChoiceCorrect = function isChoiceCorrect(step, answer) {
  var correctAnswers = step.correct;

  return correctAnswers.includes(answer);
};

var isTypingCorrect = function isTypingCorrect(step, slotId, value) {
  if (!value) {
    return false;
  }
  var answerId = getTypingAnswerId(step, slotId, value);
  return isAnswerCorrect(step, slotId, { id: answerId, text: value });
};

var getAnswers = function getAnswers(step) {
  var slots = getSlots(step);
  if (stepModel.getEvaluationType(step) === 'any' || stepModel.getInputType(step) === inputTypes.TYPING) {
    var result = {};
    slots.forEach(function (s) {
      result[s.id] = s.options[0].text;
    });
    return result;
  }
  if (stepModel.getInputType(step) !== inputTypes.SPEAKING) {
    var _result = {};
    slots.forEach(function (s, i) {
      _result[s.id] = step.correct[i];
    });
    return _result;
  }
};

export var stepClozeModel = {
  CLOZE_SPACER: CLOZE_SPACER,
  isCloze: isCloze,
  isClozeSpeech: isClozeSpeech,
  isClozeSelect: isClozeSelect,
  isClozeDragDrop: isClozeDragDrop,
  isClozeTyping: isClozeTyping,
  parseClozeData: parseClozeData,
  isSlot: isSlot,
  resolveDragAndDropAnswers: resolveDragAndDropAnswers,
  resolveSelectAnswers: resolveSelectAnswers,
  resolveSpeechAnswers: resolveSpeechAnswers,
  resolveTypingAnswers: resolveTypingAnswers,
  isAnswerCorrect: isAnswerCorrect,
  isChoiceCorrect: isChoiceCorrect,
  isTypingCorrect: isTypingCorrect,
  getAnswers: getAnswers
};